import styles from './gauge-chart.module.scss';
import {
  ChartOptions,
  ChartData,
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { ChartProps } from '../ChartWidget';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import clsx from 'clsx';
ChartJS.register(
  ArcElement,
  ChartTooltip,
  ChartLegend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  annotationPlugin
);

export function GaugeChart({ data, labels }: ChartProps) {
  const dataPoints = mapToChartData(labels ?? [], data as number[][]);
  const colors = dataPoints.map((item) => item.color);
  const gaugeLabels = labels;
  const donePercentage = dataPoints.find((item) => item.label === 'Done')?.percentage || '0%';

  const gaugeData: ChartData = {
    labels: dataPoints.map((point) => point.label),
    datasets: [
      {
        data: dataPoints.map((point) => point.value),
        backgroundColor: colors,
        borderWidth: 10,
        borderColor: colors,
        circumference: 360,
        rotation: 270,
        type: 'doughnut'
      }
    ]
  };

  const gaugeOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: false
      },
      datalabels: {
        display: false,
        formatter: (value) => {
          return `${value}%`;
        },
        color: '#000',
        font: {
          weight: 'bold',
          size: 20
        }
      },
      legend: {
        display: false
      },
      annotation: {
        annotations: {
          centerLabel: {
            type: 'label',
            content: donePercentage,
            font: {
              size: 30,
              weight: 'bold'
            },
            color: '#000'
          }
        }
      }
    },
    scales: {
      x: {
        display: false
      },
      y: {
        display: false
      }
    }
  };

  return (
    <div className={clsx(styles.gaugeWidget)}>
      <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <Chart
          id={'gaugeChartCanvas'}
          style={{ width: '100%', height: '100%' }}
          type={'doughnut'}
          data={gaugeData}
          options={gaugeOptions}
          plugins={[ChartDataLabels]}
          redraw
        />
      </div>
      <div className={styles.legend}>
        {gaugeLabels?.map((label, index) => (
          <div className={styles.legendItem} key={index}>
            <div className={styles.legendColor} style={{ backgroundColor: colors[index] }} />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface GaugeChartDataPoint {
  label: string;
  value: number;
  percentage: string;
  color: string;
}

function mapToChartData(labels: string[], data: number[][]): GaugeChartDataPoint[] {
  const defaultColors = ['#D08301', '#5694BD', '#FFCE56', '#4BC0C0', '#9966FF'];
  const chartData = data.flat();
  const total = chartData.reduce((acc, val) => acc + val, 0);

  return chartData.map((value, index) => {
    const color = defaultColors[index % defaultColors.length] || '#000000';
    return {
      label: labels[index] || '',
      value: value,
      percentage: total ? `${((value / total) * 100).toFixed(0)}%` : '0%',
      color: color
    };
  });
}

export default GaugeChart;
